import * as React from 'react'
// constants
import { ENTITIES } from 'constants/index'
// types
import { IPosition, IScheduledReview, IUser } from 'types/graphql'
// utils
import isEmpty from 'lodash/isEmpty'
import moment from 'moment'
import { getChipColor, getClosestRange, getClosestReview } from 'utils/index'

interface Context {
  nodeEvents: {
    period: any
    action: any
    review: IScheduledReview & any
    assignedPersons: IUser[] | IPosition[]
  }
}

interface Props {
  node: any
  children?: React.ReactNode
}

export const NodeContentBLContext = React.createContext<Context>({} as any)

const NodeContentBL: React.FunctionComponent<Props> = ({ children, node }) => {
  const entity = node.entity

  const action = React.useMemo(() => {
    if ([ENTITIES.PLAN_OBJECTIVE, ENTITIES.PLAN_ACTION].includes(node.entityType) && entity?.completionAt) {
      return {
        chipColor: getChipColor(entity.completionAt, entity.completionPercentage),
        chipLabel: moment(entity.completionAt).format('D MMM YY')
      }
    }
    return null
  }, [node.entityType, entity?.completionAt, entity?.completionPercentage])

  const period = React.useMemo(() => {
    if (node.entityType === ENTITIES.PLAN_KPI && entity) {
      const closestRange = getClosestRange(entity.recurrence, entity.resultTarget)
      if (closestRange) {
        return {
          chipColor: getChipColor(closestRange.dueDate, closestRange.status),
          chipLabel: closestRange.dueDate.format('D MMM YY')
        }
      }
    }
    return null
  }, [node?.entityType, entity])

  const review = React.useMemo(() => {
    const closestReview = getClosestReview(entity)
    return !isEmpty(closestReview)
      ? {
          chipColor: getChipColor(closestReview.dueDate, closestReview.status),
          chipLabel: closestReview.dueDate.format('D MMM YY')
        }
      : null
  }, [entity])

  const assignedPersons = React.useMemo(() => {
    if (entity?.ownedByUser) return [{ user: entity.ownedByUser }]
    if (entity?.ownedByPosition) return [{ position: entity.ownedByPosition }]
    return []
  }, [entity?.ownedByUser, entity?.ownedByPosition])

  const nodeEvents = { action, period, review, assignedPersons }

  const value = React.useMemo(() => ({ nodeEvents }), [nodeEvents])

  return <NodeContentBLContext.Provider value={value}>{children}</NodeContentBLContext.Provider>
}
export default NodeContentBL
